/**
 * Page-level fullscreen helpers, vendor-prefixed for older Safari / Firefox / Edge.
 * Both are safe to call at any time: a refused or unsupported request (e.g.
 * iPhone Safari, which has no element fullscreen) is swallowed and the page
 * simply stays in the normal window.
 */

export const isFullscreen = () =>
  Boolean(
    document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement,
  );

/** False where the page can't go fullscreen at all (e.g. iPhone Safari). */
export const isFullscreenSupported = () =>
  Boolean(
    document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled,
  );

const FULLSCREEN_CHANGE_EVENTS = [
  "fullscreenchange",
  "webkitfullscreenchange",
  "mozfullscreenchange",
  "MSFullscreenChange",
];

/**
 * Calls `callback` whenever the page enters or leaves fullscreen — including
 * the user pressing Esc. Returns the unsubscribe function.
 */
export const onFullscreenChange = (callback) => {
  FULLSCREEN_CHANGE_EVENTS.forEach((name) =>
    document.addEventListener(name, callback),
  );
  return () =>
    FULLSCREEN_CHANGE_EVENTS.forEach((name) =>
      document.removeEventListener(name, callback),
    );
};

/**
 * Puts the whole page in fullscreen. Must run inside a click handler —
 * browsers only allow it on a user gesture.
 */
export const enterFullscreen = () => {
  if (isFullscreen()) return;
  const elem = document.documentElement;
  const request =
    elem.requestFullscreen ||
    elem.webkitRequestFullscreen ||
    elem.mozRequestFullScreen ||
    elem.msRequestFullscreen;
  if (!request) return;
  try {
    const result = request.call(elem);
    if (result?.catch) result.catch(() => {});
  } catch (error) {
    console.error("Fullscreen request refused", error);
  }
};

/** Leaves fullscreen if the page is in it — a no-op otherwise. */
export const exitFullscreen = () => {
  if (!isFullscreen()) return;
  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;
  if (!exit) return;
  try {
    const result = exit.call(document);
    if (result?.catch) result.catch(() => {});
  } catch (error) {
    console.error("Fullscreen exit failed", error);
  }
};
